import i18n from './i18n';
import ticketOps from './serverRules/ticketOps';
import bookCatalog from './serverRules/bookCatalog';
import bookOrders from './serverRules/bookOrders';

/**
 * The backend answers in English ("Event not found", "Payment failed: …"). This maps those
 * messages to keys in the `server` namespace, so any page can show them in the admin's language
 * with `translateServerMessage(data.message)`.
 *
 * Each rule is either an exact message or a regular expression. A regular expression's named
 * groups become interpolation values, so "Payment failed: {{detail}}" keeps the detail. A message
 * no rule knows, such as one the backend already sends in Spanish, is shown as it is.
 *
 * A rule may also have `values: (groups) => ({ … })`, which turns the named groups into the
 * interpolation values. It is for parts of a message that need translating themselves:
 * `nested(detail)` translates a wrapped backend message ("Failed to process QR code: Ticket not
 * found"), and `label('eventStatus', status)` translates an enum value such as COMPLETED.
 *
 * Rules are tried in order, so a more specific rule goes before a general one that would also
 * match. The English text in locales/en/server.json reproduces the backend's message exactly
 * (enum labels included), so English mode shows what the backend sent.
 *
 * The rules below are shared by every page (auth, generic errors, scan and sell). A page area can
 * keep its own rules in serverRules/<area>.js, with its own namespace; those are tried after
 * these. Add a rule and a key to both language files whenever a page starts showing a new
 * backend message. The messages come from the backend's main branch (event-management and
 * user-management services, and the common auth aspects and exception handler).
 */

const t = (key, values) => i18n.t(key, { ns: 'server', ...values });

/** An enum value from the backend (COMPLETED, USED, SEMINAR…) in the current language. */
const label = (group, value) =>
  value === undefined || value === '' ? '' : t(`label.${group}.${value}`, { defaultValue: value });

/** A backend message wrapped inside another one, in the current language. */
const nested = (value) => translateServerMessage(value);

/** "event 42", what the backend names a scanning event it cannot find. */
const eventName = (value) => {
  const found = /^event (\d+)$/.exec(value || '');
  return found ? t('label.eventWithId', { id: found[1] }) : value;
};

// Any text, including line breaks (raw API errors can have them).
const ANY = '[\\s\\S]+';

export const SERVER_MESSAGE_RULES = [
  // ---- Auth and generic errors (common aspects, GlobalExceptionHandler, controller catch-alls)
  { match: 'Unauthorized: Invalid or expired token', key: 'auth.invalidToken' },
  { match: 'Unauthorized: Missing or invalid token', key: 'auth.missingToken' },
  { match: 'Unauthorized: Missing or invalid Authorization header', key: 'auth.missingHeader' },
  { match: 'Unauthorized: No request context', key: 'auth.noRequestContext' },
  { match: 'Authorization header is missing or invalid', key: 'auth.headerMissingOrInvalid' },
  { match: 'Access denied', key: 'auth.accessDenied' },
  { match: 'Access denied: No role found', key: 'auth.noRole' },
  { match: 'Access denied: Role not found in token', key: 'auth.roleNotInToken' },
  { match: 'Access denied: Admin privileges required', key: 'auth.adminRequired' },
  {
    match: /^Access denied: Insufficient permissions for (?<feature>[A-Z_]+)$/,
    key: 'auth.insufficientPermissions',
    values: ({ feature }) => ({ feature: label('feature', feature) }),
  },
  {
    match: /^Access denied: User role '(?<role>[^']*)' is not allowed for this endpoint$/,
    key: 'auth.roleNotAllowed',
  },
  { match: 'An error occurred during authorization', key: 'auth.authorizationError' },
  { match: 'An unexpected error occurred', key: 'common.unexpected' },
  {
    match: new RegExp(`^An unexpected error occurred: (?<detail>${ANY})$`),
    key: 'common.unexpectedWithDetail',
    values: ({ detail }) => ({ detail: nested(detail) }),
  },
  { match: 'Internal server error', key: 'common.internalError' },
  { match: 'Invalid request', key: 'common.invalidRequest' },
  { match: 'Validation failed', key: 'common.validationFailed' },
  { match: 'User not found', key: 'common.userNotFound' },

  // ---- GET em/v1/ticket/validate: TicketController
  {
    match:
      'No event selected for this scan. Please refresh the scanner app and choose the event again.',
    key: 'scan.noEventSelected',
  },
  {
    match: 'Either ticketQRCode or both iboNumber and eventId must be provided',
    key: 'scan.missingParams',
  },
  { match: 'Generic ticket validation requires QR code', key: 'scan.genericRequiresQr' },
  { match: 'Ticket validation successful', key: 'scan.success' },
  { match: 'Generic ticket validation successful', key: 'scan.genericSuccess' },
  { match: 'Invalid or tampered QR code', key: 'qr.invalid' },
  { match: 'Missing required ticket information in QR code', key: 'qr.missingInfo' },
  { match: 'Invalid or tampered generic ticket QR code', key: 'qr.genericInvalid' },
  {
    match: 'Missing required generic ticket information in QR code',
    key: 'qr.genericMissingInfo',
  },
  {
    match: new RegExp(`^Failed to process QR code: (?<detail>${ANY})$`),
    key: 'qr.failed',
    values: ({ detail }) => ({ detail: nested(detail) }),
  },
  {
    match: new RegExp(`^Failed to process generic ticket QR code: (?<detail>${ANY})$`),
    key: 'qr.genericFailed',
    values: ({ detail }) => ({ detail: nested(detail) }),
  },

  // ---- Ticket validation and purchase: TicketServiceImpl
  { match: 'Event not found', key: 'event.notFound' },
  {
    match: /^Event is not active\. Current status: (?<status>\S+)$/,
    key: 'event.notActive',
    values: ({ status }) => ({ status: label('eventStatus', status) }),
  },
  { match: 'Ticket not found', key: 'ticket.notFound' },
  { match: 'Ticket does not belong to the specified IBO', key: 'ticket.wrongIbo' },
  { match: 'Ticket does not belong to the specified event', key: 'ticket.wrongEvent' },
  { match: 'Ticket is not active. Status: USED', key: 'ticket.alreadyUsed' },
  {
    match: /^Ticket is not active\. Status: (?<status>\S+)$/,
    key: 'ticket.notActive',
    values: ({ status }) => ({ status: label('ticketStatus', status) }),
  },
  { match: 'No tickets remaining for this ticket', key: 'ticket.noneRemaining' },
  { match: 'No tickets remaining for this IBO', key: 'ticket.noneRemainingForIbo' },
  {
    match: 'No active ticket found for the provided IBO number for this event',
    key: 'ticket.noActiveTicketForIbo',
  },
  {
    match: /^Cannot validate (?<requested>\d+) tickets\. Only (?<available>\d+) tickets available\.$/,
    key: 'ticket.notEnoughToValidate',
  },
  {
    match: 'Maximum tickets (2) already used for this first contract year IBO for this event',
    key: 'ticket.firstYearMaxUsed',
  },
  // additionalInfo of a validation result
  { match: 'First contract year IBO. Maximum 2 tickets allowed.', key: 'ticket.firstYearInfo' },
  {
    match: new RegExp(
      `^This ticket is for (?<ticketEvent>${ANY})\\. You are scanning for (?<scanningEvent>${ANY})\\. It has not been used\\.$`
    ),
    key: 'ticket.wrongEventInfo',
    values: ({ ticketEvent, scanningEvent }) => ({
      ticketEvent,
      scanningEvent: eventName(scanningEvent),
    }),
  },

  // ---- IBO checks against Amway (validate and purchase)
  {
    match: /^Failed to validate IBO number: Failed to validate IBO: (?<detail>404\b[\s\S]*)$/,
    key: 'ibo.notFoundInAmway',
  },
  {
    match: new RegExp(`^Failed to validate IBO number: Failed to validate IBO: (?<detail>${ANY})$`),
    key: 'ibo.amwayCheckFailed',
  },
  {
    match: new RegExp(`^Failed to validate IBO number: (?<detail>${ANY})$`),
    key: 'ibo.checkFailed',
  },
  { match: 'IBO is not active. Cannot validate ticket.', key: 'ibo.notActive' },
  { match: 'IBO is not active. Cannot validate generic ticket.', key: 'ibo.notActiveGeneric' },
  {
    match: 'Consent not provided. Please provide consent with Amway to proceed.',
    key: 'ibo.noConsent',
  },

  // ---- Generic tickets: GenericTicketServiceImpl
  { match: 'Generic ticket not found', key: 'genericTicket.notFound' },
  { match: 'Generic ticket does not belong to the specified IBO', key: 'genericTicket.wrongIbo' },
  {
    match: 'Generic ticket does not belong to the specified event type',
    key: 'genericTicket.wrongEventType',
  },
  { match: 'Generic ticket is not active. Status: USED', key: 'genericTicket.alreadyUsed' },
  {
    match: /^Generic ticket is not active\. Status: (?<status>\S+)$/,
    key: 'genericTicket.notActive',
    values: ({ status }) => ({ status: label('ticketStatus', status) }),
  },
  { match: 'No tickets remaining for this generic ticket', key: 'genericTicket.noneRemaining' },
  // additionalInfo and eventName of a generic ticket's validation result
  {
    match: new RegExp(
      `^This is a (?<ticketType>[A-Z_]+) ticket\\. You are scanning for (?<eventName>${ANY}), which is a (?<eventType>[A-Z_]+) event\\. It has not been used\\.$`
    ),
    key: 'genericTicket.wrongEventInfo',
    values: ({ ticketType, eventName: name, eventType }) => ({
      ticketType: label('eventType', ticketType),
      eventName: name,
      eventType: label('eventType', eventType),
    }),
  },
  {
    match:
      /^Generic ticket ID: (?<ticketId>\S+), Event Type: (?<eventType>[A-Z_]+), Status: (?<status>[A-Z_]+), Original Quantity: (?<quantity>\S+?)(?<firstYear>, First Year IBO)?$/,
    key: 'genericTicket.validatedInfo',
    values: ({ ticketId, eventType, status, quantity, firstYear }) => ({
      ticketId,
      eventType: label('eventType', eventType),
      status: label('ticketStatus', status),
      quantity,
      firstYear: firstYear ? t('label.firstYearSuffix') : '',
    }),
  },
  {
    match: /^Generic (?<eventType>[A-Z_]+) Event$/,
    key: 'genericTicket.eventName',
    values: ({ eventType }) => ({ eventType: label('eventType', eventType) }),
  },

  // ---- POST em/v1/ticket/purchase: TicketController, TicketServiceImpl
  { match: 'Ticket purchased successfully', key: 'purchase.success' },
  {
    match:
      'Cash is no longer accepted. Every purchase has to go through a tracked electronic payment, so this sale cannot be recorded as cash. Cash taken before this change is unaffected and can still be refunded.',
    key: 'purchase.cashWithdrawn',
  },
  {
    match:
      'Cash is no longer accepted. Every purchase has to go through a tracked electronic payment, so this sale cannot be recorded as cash.',
    key: 'purchase.cashRefused',
  },
  {
    match: 'Purchase not allowed: IBO is not eligible for payment',
    key: 'purchase.iboNotPaymentEligible',
  },
  { match: 'Payment token is required for Square payments', key: 'purchase.paymentTokenRequired' },
  {
    match: new RegExp(`^Ticket price info not found: (?<id>${ANY})$`),
    key: 'purchase.ticketTypeNotFound',
  },
  {
    match: new RegExp(`^Selected ticket type is no longer available: (?<name>${ANY})$`),
    key: 'purchase.ticketTypeUnavailable',
  },
  {
    match: new RegExp(`^Sales period for ticket type has ended: (?<name>${ANY})$`),
    key: 'purchase.salesEnded',
  },
  { match: 'Not enough tickets available for this event', key: 'purchase.notEnoughTickets' },

  // ---- Payment failures: SquarePaymentServiceImpl, SquareErrorMapper (messages_en.properties)
  {
    match: new RegExp(`^Payment failed: (?<detail>${ANY})$`),
    key: 'payment.failed',
    values: ({ detail }) => ({ detail: nested(detail) }),
  },
  { match: 'Payment processing failed', key: 'payment.processingFailed' },
  {
    match: 'Your card was declined. Please try a different card or contact your bank.',
    key: 'payment.cardDeclined',
  },
  {
    match: 'Your card was declined. Please contact your bank for assistance.',
    key: 'payment.cardDeclinedCallIssuer',
  },
  {
    match:
      'Additional verification is required. Please contact your bank or try a different card.',
    key: 'payment.verificationRequired',
  },
  {
    match: 'The security code (CVV) is incorrect. Please check and try again.',
    key: 'payment.cvvFailure',
  },
  {
    match: 'The billing address does not match the card on file. Please verify your address.',
    key: 'payment.addressMismatch',
  },
  { match: 'Your card has expired. Please use a different card.', key: 'payment.cardExpired' },
  {
    match: 'Insufficient funds on your card. Please try a different payment method.',
    key: 'payment.insufficientFunds',
  },
  {
    match: 'This card type is not supported. Please use a different card.',
    key: 'payment.cardNotSupported',
  },
  {
    match: 'Invalid card number. Please check your card details and try again.',
    key: 'payment.invalidCard',
  },
  { match: 'Your payment session has expired. Please try again.', key: 'payment.sessionExpired' },
  {
    match:
      'This transaction exceeds the allowed limit. Please try a smaller amount or contact your bank.',
    key: 'payment.transactionLimit',
  },
  {
    match:
      'A temporary issue occurred with payment processing. Please try again in a few moments.',
    key: 'payment.temporary',
  },
  {
    match: 'The postal/zip code is invalid. Please check and try again.',
    key: 'payment.invalidPostalCode',
  },
  {
    match: 'Too many incorrect PIN attempts. Please contact your bank.',
    key: 'payment.pinTriesExceeded',
  },
  { match: 'Please insert your chip card instead of swiping.', key: 'payment.chipRequired' },
  { match: 'The PIN entered is incorrect. Please try again.', key: 'payment.invalidPin' },
  {
    match: 'Manual card entry is not supported. Please use a different payment method.',
    key: 'payment.manualEntryNotSupported',
  },
  {
    match: 'Payment could not be processed. Please try again or use a different payment method.',
    key: 'payment.generic',
  },

  // ---- GET em/v1/events/upcoming, GET em/v1/admin/teams
  { match: 'Upcoming events retrieved successfully', key: 'event.upcomingRetrieved' },
  { match: 'Teams retrieved successfully', key: 'team.retrieved' },

  // ---- GET em/v1/volunteer/cash-summary, cash-transactions: VolunteerCashController
  { match: 'Cash summary fetched successfully', key: 'cash.summaryFetched' },
  {
    match: new RegExp(`^Failed to fetch cash summary: (?<detail>${ANY})$`),
    key: 'cash.summaryFailed',
    values: ({ detail }) => ({ detail: nested(detail) }),
  },
  { match: 'Cash transactions fetched successfully', key: 'cash.transactionsFetched' },
  {
    match: new RegExp(`^Failed to fetch cash transactions: (?<detail>${ANY})$`),
    key: 'cash.transactionsFailed',
    values: ({ detail }) => ({ detail: nested(detail) }),
  },
  // eventName of a cash breakdown or transaction the backend could not name
  { match: 'Unknown Event', key: 'cash.unknownEvent' },
  { match: 'Generic Ticket', key: 'cash.genericTicket' },

  // ---- GET um/v1/admin/user/get: AdminUserController, UserServiceImpl.getUserForAdmin
  { match: 'Either userId or iboNumber must be provided', key: 'user.idOrIboRequired' },
  {
    match: new RegExp(`^User not found with IBO number: (?<iboNumber>${ANY})$`),
    key: 'user.notFoundByIbo',
  },
  { match: /^User not found with ID: (?<id>\S+)$/, key: 'user.notFoundById' },
  { match: 'User retrieved successfully', key: 'user.retrieved' },

  // ---- GET um/v1/admin/user/validate-ticket-eligibility (message, data.message, data.additionalInfo)
  { match: 'IBO Number is required', key: 'eligibility.iboRequired' },
  { match: 'IBO eligibility validated successfully', key: 'eligibility.validated' },
  { match: 'IBO is not eligible for ticket purchase', key: 'eligibility.notEligibleShort' },
  {
    match: 'An unexpected error occurred while validating IBO eligibility',
    key: 'eligibility.unexpected',
  },
  {
    match: 'IBO is eligible to purchase tickets. All requirements are met.',
    key: 'eligibility.eligible',
  },
  {
    match: 'Note: This IBO is in their first contract year. Some restrictions may apply.',
    key: 'eligibility.firstYearNote',
  },
  {
    match: 'Unable to validate IBO eligibility. Please try again later.',
    key: 'eligibility.unableToValidate',
  },
  {
    // "IBO is not eligible to purchase tickets. " followed by whichever reasons apply.
    match:
      /^IBO is not eligible to purchase tickets\.(?: Status is '(?<status>[^']*)' \(required: 'Active' or 'Yes'\)\.)?(?: Payment eligibility is '(?<payment>[^']*)' \(required: 'Yes'\)\.)?(?: Consent is '(?<consent>[^']*)' \(required: 'Yes'\)\.)?(?<firstYear> This IBO is in their first contract year\.)?$/,
    key: 'eligibility.notEligible',
    values: ({ status, payment, consent, firstYear }) => ({
      reasons: [
        status !== undefined && t('eligibility.reason.status', { value: status }),
        payment !== undefined && t('eligibility.reason.payment', { value: payment }),
        consent !== undefined && t('eligibility.reason.consent', { value: consent }),
        firstYear !== undefined && t('eligibility.reason.firstYear'),
      ]
        .filter(Boolean)
        .join(' '),
    }),
  },
];

/** The helpers an area's rules get, bound to that area's namespace. */
const helpersFor = (ns) => {
  const areaT = (key, values) => i18n.t(key, { ns, ...values });
  return {
    t: areaT,
    label: (group, value) =>
      value === undefined || value === ''
        ? ''
        : areaT(`label.${group}.${value}`, { defaultValue: label(group, value) }),
    nested,
    ANY,
  };
};

/** Every rule, shared ones first, each group with the namespace its keys live in. */
export const SERVER_RULE_GROUPS = [
  { ns: 'server', rules: SERVER_MESSAGE_RULES },
  ...[ticketOps, bookCatalog, bookOrders].map((area) => ({
    ns: area.ns,
    rules: area.rules(helpersFor(area.ns)),
  })),
];

const matchRule = (message) => {
  for (const { ns, rules } of SERVER_RULE_GROUPS) {
    for (const rule of rules) {
      if (typeof rule.match === 'string') {
        if (rule.match === message) return { ns, key: rule.key, values: {} };
      } else {
        const found = rule.match.exec(message);
        if (found) {
          // A named group that didn't take part in the match is undefined; show it as nothing.
          const groups = {};
          Object.entries(found.groups || {}).forEach(([name, value]) => {
            groups[name] = value === undefined ? '' : value;
          });
          const values = rule.values ? rule.values({ ...found.groups }) : groups;
          return { ns, key: rule.key, values };
        }
      }
    }
  }
  return null;
};

/**
 * The backend's message in the current language. Returns `fallback` when there is no message, and
 * the message unchanged when no rule knows it.
 */
export const translateServerMessage = (message, fallback = '') => {
  if (message === undefined || message === null || message === '') return fallback;
  const text = String(message).trim();
  const found = matchRule(text);
  if (!found) return text;
  return String(
    i18n.t(found.key, { ns: found.ns, ...found.values, defaultValue: text })
  ).trim();
};

export default translateServerMessage;
